/**
 * Expanded Question Bank: Domain 1 — Abstract Reasoning & Patterns
 * 15 Additional High-Entropy Template Families with Scalable Vector Stimuli
 */

import { render3x3Matrix, renderHorizontalSeries, renderOptionSvg, DrawShapes, wrapSvg } from './svg-builder.js';

export const EXPANDED_ABSTRACT_TEMPLATES = [
  // 1. Diagonal Matrix Reflection
  {
    id: 'abs_diag_mirror',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const symbols = ['circle', 'triangle', 'square', 'diamond', 'star', 'cross'];
      const [diagSym, s1, s2, s3] = prng.sample(symbols, 4);

      const drawSym = (sym) => (s) => {
        if (sym === 'circle') return DrawShapes.circle(s * 0.9, true, '#38bdf8', '#38bdf8');
        if (sym === 'triangle') return DrawShapes.triangle(s * 0.9, true, '#83C8F0', '#83C8F0');
        if (sym === 'square') return DrawShapes.square(s * 0.9, true, '#6666E8', '#6666E8');
        if (sym === 'diamond') return DrawShapes.diamond(s * 0.9, true, '#ec4899', '#ec4899');
        if (sym === 'star') return DrawShapes.star(s * 0.9, true, '#f59e0b', '#f59e0b');
        return DrawShapes.cross(s * 0.9, '#10b981', 3);
      };

      // Symmetric matrix across main diagonal: A[r][c] = A[c][r], with uniform main diagonal
      const grid = [
        [diagSym, s1, s2],
        [s1, diagSym, s3],
        [s2, s3, diagSym]
      ];

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          cells.push(drawSym(grid[r][c]));
        }
      }

      const correctSym = diagSym;
      const distractors = [s1, s2, s3];
      const allChoices = prng.shuffle([correctSym, ...distractors]);
      const correctIndex = allChoices.indexOf(correctSym);

      const options = allChoices.map((sym, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawSym(sym), 110, `Option ${idx + 1}`),
        value: sym
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتميز المصفوفة بتماثل انعكاسي قطري (كل عنصر عند الموضع [صف, عمود] يطابق نظيره عند [عمود, صف]). ما هو الشكل الناقص في الخلية الأخيرة؟',
        questionText_en: 'The matrix exhibits diagonal reflection symmetry (cell [r, c] mirrors cell [c, r]). Which symbol completes the bottom-right position?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `المصفوفة متماثلة انعكاسيًا حول القطر الرئيسي (كل عنصر [صف, عمود] يطابق نظيره عند [عمود, صف])، وتتطابق عناصر القطر الرئيسي نفسه، مما يحدد أن الشكل الناقص في الخلية الأخيرة [2,2] هو نفس عنصر القطر الرئيسي.`,
        explanation_en: `The matrix exhibits reflection symmetry across the main diagonal (A[r, c] = A[c, r]), and all elements along the main diagonal are identical. Thus, cell [2, 2] completes the diagonal.`
      };
    }
  },

  // 2. Concentric Nested Ring Progression
  {
    id: 'abs_concentric_growth',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const step = difficulty === 3 ? 2 : 1;
      const startCount = prng.int(1, 2);

      const drawConcentric = (count) => (s) => {
        let out = '';
        for (let i = 1; i <= count; i++) {
          const r = (s * 0.44 * i) / Math.max(count, 4);
          const isOuter = (i === count);
          out += `<circle cx="0" cy="0" r="${r.toFixed(1)}" fill="${isOuter ? 'rgba(56, 189, 248, 0.2)' : 'none'}" stroke="#38bdf8" stroke-width="2.2"/>`;
        }
        return out;
      };

      const items = [];
      for (let i = 0; i < 4; i++) {
        items.push(drawConcentric(startCount + i * step));
      }

      const correctCount = startCount + 4 * step;
      const distractors = [correctCount - 1, correctCount + 1, correctCount + 2].filter(c => c > 0);
      const allCounts = prng.shuffle([correctCount, ...distractors]);
      const correctIndex = allCounts.indexOf(correctCount);

      const options = allCounts.map((cnt, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawConcentric(cnt), 110, `Rings ${cnt}`),
        value: cnt
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يزداد عدد الحلقات متحدة المركز وفق متتالية منتظمة. ما هو الشكل الذي يمثل العنصر الخامس؟',
        questionText_en: 'The number of concentric rings advances in a steady sequence. Which figure represents the fifth term?',
        stimulusHtml: renderHorizontalSeries(items, 360),
        options,
        correctIndex,
        explanation_ar: `يزيد عدد الحلقات بمقدار ${step} حلقة في كل خطوة، ما يجعل الشكل الخامس يحتوي على ${correctCount} حلقات بدقة.`,
        explanation_en: `The concentric ring count advances by ${step} at each step, arriving at ${correctCount} rings for the 5th term.`
      };
    }
  },

  // 3. Orbiting Satellite Quadrant Shift
  {
    id: 'abs_orbiting_satellite',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const stepAngle = prng.pick([45, 90]);
      const baseAngle = prng.int(0, 3) * 45;

      const drawOrbit = (angle) => (s) => {
        const r = s * 0.38;
        const rad = (angle * Math.PI) / 180;
        const sx = (Math.cos(rad) * r).toFixed(1);
        const sy = (Math.sin(rad) * r).toFixed(1);
        return `
          <circle cx="0" cy="0" r="${(s * 0.2).toFixed(1)}" fill="rgba(102, 102, 232, 0.4)" stroke="#6666E8" stroke-width="2"/>
          <circle cx="0" cy="0" r="${r.toFixed(1)}" fill="none" stroke="rgba(255, 255, 255, 0.2)" stroke-width="1.2" stroke-dasharray="3 3"/>
          <circle cx="${sx}" cy="${sy}" r="${(s * 0.08).toFixed(1)}" fill="#38bdf8" stroke="#ffffff" stroke-width="1.5"/>
        `;
      };

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const a = (baseAngle + (r + c) * stepAngle) % 360;
          cells.push(drawOrbit(a));
        }
      }

      const correctAngle = (baseAngle + 4 * stepAngle) % 360;
      const possibleAngles = [0, 45, 90, 135, 180, 225, 270, 315].filter(a => a !== correctAngle);
      const distractors = prng.sample(possibleAngles, 3);
      const allAngles = prng.shuffle([correctAngle, ...distractors]);
      const correctIndex = allAngles.indexOf(correctAngle);

      const options = allAngles.map((a, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawOrbit(a), 110, `Angle ${a}`),
        value: a
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يدور القمر التابع حول المركز بزاوية ثابتة عبر صفوف وأعمدة المصفوفة. ما هو الموضع الصحيح للقمر في الخلية الأخيرة؟',
        questionText_en: 'The satellite orbits the central hub at a consistent angular step across the matrix. What is its correct position in the final cell?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `يتقدم المدار بمقدار ${stepAngle}° في اتجاه دوران عقارب الساعة عبر كل صف وعمود، ليصل إلى زاوية ${correctAngle}° في الخلية الأخيرة.`,
        explanation_en: `The satellite steps clockwise by ${stepAngle}° across rows and columns, reaching ${correctAngle}° in the final cell.`
      };
    }
  },

  // 4. Polygon Vertex Arithmetic (Col3 = Col1 + Col2)
  {
    id: 'abs_polygon_sum',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawPoly = (sides) => (s) => {
        if (sides === 2) {
          return `<line x1="${-s * 0.35}" y1="0" x2="${s * 0.35}" y2="0" stroke="#38bdf8" stroke-width="3"/>`;
        }
        const pts = [];
        const r = s * 0.42;
        for (let i = 0; i < sides; i++) {
          const a = (i * 2 * Math.PI) / sides - Math.PI / 2;
          pts.push(`${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`);
        }
        return `<polygon points="${pts.join(' ')}" fill="rgba(56, 189, 248, 0.2)" stroke="#38bdf8" stroke-width="2.4"/>`;
      };

      const rowsSides = [
        [3, 3, 6], // Triangle (3) + Triangle (3) = Hexagon (6)
        [3, 4, 7], // Triangle (3) + Square (4) = Heptagon (7)
        [4, 4, 8]  // Square (4) + Square (4) = Octagon (8)
      ];

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          cells.push(drawPoly(rowsSides[r][c]));
        }
      }

      const correctSides = 8;
      const distractors = [6, 7, 9];
      const allSides = prng.shuffle([correctSides, ...distractors]);
      const correctIndex = allSides.indexOf(correctSides);

      const options = allSides.map((sd, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawPoly(sd), 110, `Sides ${sd}`),
        value: sd
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'في كل صف، عدد أضلاع الشكل في العمود الثالث يساوي مجموع أضلاع الشكلين في العمودين الأول والثاني. ما هو الشكل المتمم؟',
        questionText_en: 'In each row, the polygon in column 3 has sides equal to the sum of sides in columns 1 and 2. Which shape completes the pattern?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: 'الصف الأخير يجمع مربعين (4 أضلاع + 4 أضلاع) ليكون الشكل المطلوب مضلعًا ثماني الأضلاع (8 أضلاع).',
        explanation_en: 'The bottom row combines two 4-sided squares (4 + 4), requiring an 8-sided octagon for the missing cell.'
      };
    }
  },

  // 5. Dual Opposing Clock Hands
  {
    id: 'abs_dual_clock_hands',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const stepHour = prng.pick([30, 60]);
      const stepMin = stepHour * 2;
      const startHour = prng.int(0, 3) * 30;
      const startMin = prng.int(0, 3) * 60;

      const drawClock = (degH, degM) => (s) => {
        const radH = (degH * Math.PI) / 180;
        const radM = (degM * Math.PI) / 180;
        const lenH = s * 0.26;
        const lenM = s * 0.38;
        return `
          <circle cx="0" cy="0" r="${(s * 0.44).toFixed(1)}" fill="rgba(14, 22, 40, 0.6)" stroke="#38bdf8" stroke-width="2"/>
          <line x1="0" y1="0" x2="${(Math.sin(radH) * lenH).toFixed(1)}" y2="${(-Math.cos(radH) * lenH).toFixed(1)}" stroke="#6666E8" stroke-width="3.5" stroke-linecap="round"/>
          <line x1="0" y1="0" x2="${(Math.sin(radM) * lenM).toFixed(1)}" y2="${(-Math.cos(radM) * lenM).toFixed(1)}" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round"/>
          <circle cx="0" cy="0" r="3.5" fill="#ffffff"/>
        `;
      };

      const items = [];
      for (let i = 0; i < 4; i++) {
        items.push(drawClock((startHour + i * stepHour) % 360, (startMin + i * stepMin) % 360));
      }

      const correctH = (startHour + 4 * stepHour) % 360;
      const correctM = (startMin + 4 * stepMin) % 360;

      const distractors = [
        drawClock((correctH + 60) % 360, correctM),
        drawClock(correctH, (correctM + 60) % 360),
        drawClock((correctH + 180) % 360, (correctM + 180) % 360)
      ];

      const allFns = prng.shuffle([drawClock(correctH, correctM), ...distractors]);
      const correctIndex = allFns.findIndex(fn => fn(60) === drawClock(correctH, correctM)(60));

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 110, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يتحرك عقربا الساعة بنسب سرعة منتظمة (العقرب الطويل يتقدم بمعدل ضعف العقرب القصير). ما هو موضع العقربين في الساعة الخامسة؟',
        questionText_en: 'Two clock hands rotate at linked gear speeds (the minute hand advances at double the angular rate of the hour hand). What is the 5th state?',
        stimulusHtml: renderHorizontalSeries(items, 360),
        options,
        correctIndex,
        explanation_ar: `يتقدم العقرب القصير بمقدار ${stepHour}° بينما يتقدم العقرب الطويل بمقدار ${stepMin}° في كل خطوة.`,
        explanation_en: `The short hand steps by ${stepHour}° while the long hand advances by ${stepMin}° per step.`
      };
    }
  },

  // 6. Tangram Quadrant Shading Shift
  {
    id: 'abs_quadrant_rotation',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const activeStart = prng.int(0, 3);

      const drawQuadrants = (active) => (s) => {
        const half = s * 0.42;
        const coords = [
          [-half, -half, 0, 0], // Top-Left (0)
          [0, -half, half, 0],   // Top-Right (1)
          [0, 0, half, half],    // Bottom-Right (2)
          [-half, 0, 0, half]    // Bottom-Left (3)
        ];
        let out = `<rect x="${-half}" y="${-half}" width="${half * 2}" height="${half * 2}" fill="none" stroke="#6666E8" stroke-width="2"/>`;
        out += `<line x1="${-half}" y1="0" x2="${half}" y2="0" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>`;
        out += `<line x1="0" y1="${-half}" x2="0" y2="${half}" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>`;

        const [x1, y1, x2, y2] = coords[active % 4];
        out += `<rect x="${x1}" y="${y1}" width="${Math.abs(x2 - x1)}" height="${Math.abs(y2 - y1)}" fill="#38bdf8"/>`;
        return out;
      };

      const cells = [];
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          cells.push(drawQuadrants((activeStart + r + c) % 4));
        }
      }

      const correctQuadrant = (activeStart + 4) % 4;
      const otherQuadrants = [0, 1, 2, 3].filter(q => q !== correctQuadrant);
      const allChoices = prng.shuffle([correctQuadrant, ...otherQuadrants]);
      const correctIndex = allChoices.indexOf(correctQuadrant);

      const options = allChoices.map((q, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawQuadrants(q), 110, `Quadrant ${q}`),
        value: q
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'ينتقل الربع المظلل داخل المربع في اتجاه عقارب الساعة مع كل خطوة أفقية ورأسية. ما هو الشكل الذي يكمل المصفوفة؟',
        questionText_en: 'The shaded quadrant shifts clockwise through rows and columns. Which square logically completes the matrix?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: `يدور الربع المظلل دورة واحدة كاملة بمقدار 90° في اتجاه عقارب الساعة في كل خطوة، ليعود إلى الربع رقم ${correctQuadrant + 1}.`,
        explanation_en: `The shaded quadrant rotates 90° clockwise at each step, returning to quadrant ${correctQuadrant + 1} at the final position.`
      };
    }
  },

  // 7. Arrow Head Count Series (1, 2, 3, 4, find 5)
  {
    id: 'abs_arrow_heads_progression',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const rot = prng.pick([0, 90, 180, 270]);

      const drawArrowWithHeads = (count) => (s) => {
        let out = `<g transform="rotate(${rot})">`;
        const len = s * 0.44;
        out += `<line x1="${-len}" y1="0" x2="${len}" y2="0" stroke="#38bdf8" stroke-width="3" stroke-linecap="round"/>`;
        for (let i = 0; i < count; i++) {
          const tipX = len - i * (s * 0.14);
          const hSize = s * 0.16;
          out += `<polyline points="${tipX - hSize},${-hSize} ${tipX},0 ${tipX - hSize},${hSize}" fill="none" stroke="#38bdf8" stroke-width="3" stroke-linecap="round"/>`;
        }
        out += `</g>`;
        return out;
      };

      const items = [1, 2, 3, 4].map(c => drawArrowWithHeads(c));
      const correctCount = 5;
      const distractors = [3, 4, 6];
      const allCounts = prng.shuffle([correctCount, ...distractors]);
      const correctIndex = allCounts.indexOf(correctCount);

      const options = allCounts.map((cnt, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawArrowWithHeads(cnt), 110, `Heads ${cnt}`),
        value: cnt
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يزداد عدد رؤوس السهم التراكمية في هذا التسلسل بمعدل رأس واحد في كل مرحلة. ما هو الشكل التالي؟',
        questionText_en: 'The number of arrowheads increments by 1 at each stage. What is the next arrow in this progression?',
        stimulusHtml: renderHorizontalSeries(items, 360),
        options,
        correctIndex,
        explanation_ar: 'يزيد عدد رؤوس السهم خطيًا (1، 2، 3، 4)، لذا يجب أن يحتوي السهم الخامس على 5 رؤوس بالضبط.',
        explanation_en: 'Arrowhead count advances by 1 at each term (1, 2, 3, 4), requiring exactly 5 heads for the next term.'
      };
    }
  },

  // 8. Crossed Line Intersections Math
  {
    id: 'abs_line_intersection_count',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawGrid = (hLines, vLines) => (s) => {
        const half = s * 0.42;
        let out = `<rect x="${-half}" y="${-half}" width="${half * 2}" height="${half * 2}" rx="8" fill="rgba(14, 22, 40, 0.6)" stroke="#6666E8" stroke-width="2"/>`;
        for (let i = 1; i <= hLines; i++) {
          const y = -half + (half * 2 * i) / (hLines + 1);
          out += `<line x1="${-half}" y1="${y}" x2="${half}" y2="${y}" stroke="#38bdf8" stroke-width="2.4"/>`;
        }
        for (let j = 1; j <= vLines; j++) {
          const x = -half + (half * 2 * j) / (vLines + 1);
          out += `<line x1="${x}" y1="${-half}" x2="${x}" y2="${half}" stroke="#38bdf8" stroke-width="2.4"/>`;
        }
        return out;
      };

      // 3x3 matrix: Row 1 has (1,1)->1pt, (1,2)->2pt, (1,3)->3pt
      // Row 2 has (2,1)->2pt, (2,2)->4pt, (2,3)->6pt
      // Row 3 has (3,1)->3pt, (3,2)->6pt, (3,3)->9pt
      const cells = [];
      for (let r = 1; r <= 3; r++) {
        for (let c = 1; c <= 3; c++) {
          cells.push(drawGrid(r, c));
        }
      }

      const correctH = 3;
      const correctV = 3;

      const distractors = [
        drawGrid(2, 3),
        drawGrid(3, 2),
        drawGrid(4, 3)
      ];

      const allFns = prng.shuffle([drawGrid(correctH, correctV), ...distractors]);
      const correctIndex = allFns.findIndex(fn => fn(60) === drawGrid(correctH, correctV)(60));

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 110, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يمثل عدد الخطوط الأفقية رقم الصف وعدد الخطوط الرأسية رقم العمود. ما هو الشكل الذي يكمل الموضع الأخير في المصفوفة؟',
        questionText_en: 'Horizontal line count maps to row index while vertical line count maps to column index. Which figure completes the matrix?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: 'الخلية الأخيرة تقع في الصف الثالث والعمود الثالث، لذا تحتوي على 3 خطوط أفقية متقاطعة مع 3 خطوط رأسية.',
        explanation_en: 'The final cell is in row 3, column 3, requiring 3 horizontal lines intersecting 3 vertical lines.'
      };
    }
  },

  // 9. Diagonal Shading Wedge Series
  {
    id: 'abs_wedge_series',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const step = 45;
      const startDeg = prng.int(0, 3) * 45;

      const drawWedge = (deg) => (s) => {
        const r = s * 0.42;
        const rad1 = (deg * Math.PI) / 180;
        const rad2 = ((deg + 90) * Math.PI) / 180;
        const x1 = (Math.cos(rad1) * r).toFixed(1);
        const y1 = (Math.sin(rad1) * r).toFixed(1);
        const x2 = (Math.cos(rad2) * r).toFixed(1);
        const y2 = (Math.sin(rad2) * r).toFixed(1);
        return `
          <circle cx="0" cy="0" r="${r.toFixed(1)}" fill="none" stroke="#6666E8" stroke-width="2"/>
          <path d="M 0 0 L ${x1} ${y1} A ${r.toFixed(1)} ${r.toFixed(1)} 0 0 1 ${x2} ${y2} Z" fill="#38bdf8"/>
        `;
      };

      const items = [0, 1, 2, 3].map(i => drawWedge((startDeg + i * step) % 360));
      const correctDeg = (startDeg + 4 * step) % 360;
      const distractors = [(correctDeg + 90) % 360, (correctDeg + 180) % 360, (correctDeg + 270) % 360];
      const allDegs = prng.shuffle([correctDeg, ...distractors]);
      const correctIndex = allDegs.indexOf(correctDeg);

      const options = allDegs.map((d, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawWedge(d), 110, `Wedge ${d}`),
        value: d
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يدور القطاع المظلل (ربع دائرة) بمقدار 45 درجة في كل خطوة. ما هو الشكل الخامس في المتتابعة؟',
        questionText_en: 'The shaded 90° wedge rotates clockwise by 45° at each step. What is the 5th term in this sequence?',
        stimulusHtml: renderHorizontalSeries(items, 360),
        options,
        correctIndex,
        explanation_ar: `يدور القطاع بمقدار 45° مع عقارب الساعة في كل خطوة ليصل إلى زاوية ${correctDeg}° في الشكل الخامس.`,
        explanation_en: `The 90° wedge steps by 45° clockwise per term, arriving at orientation ${correctDeg}° for the 5th term.`
      };
    }
  },

  // 10. Star Vertices Count (4, 5, 6, 7, 8)
  {
    id: 'abs_star_points_progression',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const drawNPointStar = (points) => (s) => {
        const pts = [];
        const rOuter = s * 0.44;
        const rInner = s * 0.20;
        const total = points * 2;
        for (let i = 0; i < total; i++) {
          const r = i % 2 === 0 ? rOuter : rInner;
          const a = (i * Math.PI) / points - Math.PI / 2;
          pts.push(`${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`);
        }
        return `<polygon points="${pts.join(' ')}" fill="rgba(56, 189, 248, 0.25)" stroke="#38bdf8" stroke-width="2.4"/>`;
      };

      const startPoints = prng.pick([3, 4]);
      const items = [0, 1, 2, 3].map(i => drawNPointStar(startPoints + i));
      const correctPoints = startPoints + 4;
      const distractors = [correctPoints - 1, correctPoints + 1, correctPoints + 2];
      const allPoints = prng.shuffle([correctPoints, ...distractors]);
      const correctIndex = allPoints.indexOf(correctPoints);

      const options = allPoints.map((pts, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawNPointStar(pts), 110, `Star ${pts}`),
        value: pts
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'يزداد عدد رؤوس النجمة بمقدار رأس واحد في كل مرحلة. ما هو شكل النجمة الخامسة؟',
        questionText_en: 'The number of points on the star increments by 1 at each stage. What is the 5th star in the progression?',
        stimulusHtml: renderHorizontalSeries(items, 360),
        options,
        correctIndex,
        explanation_ar: `تزيد رؤوس النجمة خطيًا بمقدار 1 في كل خطوة، ما يجعل النجمة الخامسة ذات ${correctPoints} رؤوس.`,
        explanation_en: `The star points increment by 1 at each term, requiring a ${correctPoints}-pointed star for the 5th term.`
      };
    }
  },

  // 11. Overlapping Geometric Venn Subtraction
  {
    id: 'abs_venn_subtraction',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawTwoCircles = (shadedRegion) => (s) => {
        const r = s * 0.26;
        const d = s * 0.16;
        let out = `
          <circle cx="${-d}" cy="0" r="${r}" fill="${shadedRegion === 'left' || shadedRegion === 'both' ? '#38bdf8' : 'none'}" stroke="#38bdf8" stroke-width="2"/>
          <circle cx="${d}" cy="0" r="${r}" fill="${shadedRegion === 'right' || shadedRegion === 'both' ? '#6666E8' : 'none'}" stroke="#6666E8" stroke-width="2"/>
        `;
        return out;
      };

      // Col 1: Left shaded, Col 2: Right shaded, Col 3: Both shaded
      const cells = [
        drawTwoCircles('left'), drawTwoCircles('right'), drawTwoCircles('both'),
        drawTwoCircles('right'), drawTwoCircles('left'), drawTwoCircles('both'),
        drawTwoCircles('left'), drawTwoCircles('right'), drawTwoCircles('both')
      ];

      const correctFn = drawTwoCircles('both');
      const distractors = [
        drawTwoCircles('left'),
        drawTwoCircles('right'),
        drawTwoCircles('none')
      ];

      const allFns = prng.shuffle([correctFn, ...distractors]);
      const correctIndex = allFns.findIndex(fn => fn(60) === correctFn(60));

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 110, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'العمود الثالث يمثل اتحاد تظليل الدائرتين في العمودين الأول والثاني في كل صف. ما هو الشكل المتمم؟',
        questionText_en: 'Column 3 represents the Boolean union of shaded circles from columns 1 and 2 in each row. Which shape completes the matrix?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: 'تتحد المنطقتان المظللتان في العمودين الأول والثاني لينتج في العمود الثالث تظليل الدائرتين معًا.',
        explanation_en: 'Combining the shaded circles from the first two columns produces both circles shaded in the third column.'
      };
    }
  },

  // 12. Nested Polygon Sidedness Difference (Outer - Inner)
  {
    id: 'abs_nested_polygon_difference',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const drawNestedPoly = (outerSides, innerSides) => (s) => {
        const drawSingle = (sides, radius, color) => {
          const pts = [];
          for (let i = 0; i < sides; i++) {
            const a = (i * 2 * Math.PI) / sides - Math.PI / 2;
            pts.push(`${(Math.cos(a) * radius).toFixed(1)},${(Math.sin(a) * radius).toFixed(1)}`);
          }
          return `<polygon points="${pts.join(' ')}" fill="none" stroke="${color}" stroke-width="2.4"/>`;
        };
        return drawSingle(outerSides, s * 0.42, '#38bdf8') + drawSingle(innerSides, s * 0.22, '#6666E8');
      };

      // Series where (outer - inner) is constant = 2:
      // Triangle(3) inside Pentagon(5) -> 5 - 3 = 2
      // Square(4) inside Hexagon(6) -> 6 - 4 = 2
      // Pentagon(5) inside Heptagon(7) -> 7 - 5 = 2
      // Hexagon(6) inside Octagon(8) -> 8 - 6 = 2
      const items = [
        drawNestedPoly(5, 3),
        drawNestedPoly(6, 4),
        drawNestedPoly(7, 5),
        drawNestedPoly(8, 6)
      ];

      const correctFn = drawNestedPoly(9, 7);
      const distractors = [
        drawNestedPoly(9, 6), // Diff = 3
        drawNestedPoly(8, 7), // Diff = 1
        drawNestedPoly(10, 7) // Diff = 3
      ];

      const allFns = prng.shuffle([correctFn, ...distractors]);
      const correctIndex = allFns.findIndex(fn => fn(60) === correctFn(60));

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 110, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'الفرق بين عدد أضلاع الإطار الخارجي والشكل الداخلي ثابت دائماً ويساوي 2 مع زيادة الأضلاع بمقدار 1 في كل خطوة. ما هو الشكل الخامس؟',
        questionText_en: 'The difference between outer and inner polygon sides is constant (+2) while each advances by 1 side per step. What is the 5th term?',
        stimulusHtml: renderHorizontalSeries(items, 360),
        options,
        correctIndex,
        explanation_ar: 'الشكل الخامس يتكون من مضلع تساعي خارجي (9 أضلاع) بداخله مضلع سباعي (7 أضلاع) ليبقى الفارق الثابت 2.',
        explanation_en: 'The 5th term requires a 9-sided outer polygon with a 7-sided inner polygon to preserve the constant difference of 2.'
      };
    }
  },

  // 13. Dot Grid Wave Progression
  {
    id: 'abs_dot_wave_progression',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawWave = (offset) => (s) => {
        let out = '';
        const dots = 5;
        const spacing = (s * 0.8) / (dots - 1);
        for (let i = 0; i < dots; i++) {
          const x = -s * 0.4 + i * spacing;
          const y = Math.sin(((i + offset) * Math.PI) / 2) * (s * 0.28);
          out += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${(s * 0.06).toFixed(1)}" fill="#38bdf8"/>`;
        }
        return out;
      };

      const items = [0, 1, 2, 3].map(i => drawWave(i));
      const correctOffset = 4;
      const distractors = [1, 2, 3];
      const allOffsets = prng.shuffle([correctOffset, ...distractors]);
      const correctIndex = allOffsets.indexOf(correctOffset);

      const options = allOffsets.map((off, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(drawWave(off), 110, `Wave ${off}`),
        value: off
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتحرك موجة النقاط رأسياً في إزاحة موجية دورية منتظمة عبر الخطوات. ما هو الشكل التالي الذي يكمل الدورة؟',
        questionText_en: 'The sinusoidal dot wave shifts vertically in periodic cycles. Which pattern accurately completes the wave sequence?',
        stimulusHtml: renderHorizontalSeries(items, 360),
        options,
        correctIndex,
        explanation_ar: 'تكمل الموجة دورة كاملة كل 4 خطوات، وبالتالي فإن الخطوة الخامسة تطابق تماماً الخطوة الأولى في الدورة.',
        explanation_en: 'The sine wave has a cycle period of 4 steps, making step 5 identical to the initial wave offset.'
      };
    }
  },

  // 14. Linear Symmetry Fold (Bilateral Butterfly)
  {
    id: 'abs_bilateral_butterfly',
    domain: 'abstract_reasoning',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const wings = prng.pick([3, 4, 5]);

      const drawBilateral = (w, isSymmetric) => (s) => {
        const half = s * 0.4;
        let out = `<line x1="0" y1="${-half}" x2="0" y2="${half}" stroke="#ffffff" stroke-width="1.8" stroke-dasharray="3 3"/>`;
        for (let i = 1; i <= w; i++) {
          const y = -half + (half * 2 * i) / (w + 1);
          const lenL = (s * 0.3 * i) / w;
          const lenR = isSymmetric ? lenL : (s * 0.15);
          out += `<line x1="0" y1="${y}" x2="${-lenL}" y2="${y}" stroke="#38bdf8" stroke-width="2.6" stroke-linecap="round"/>`;
          out += `<line x1="0" y1="${y}" x2="${lenR}" y2="${y}" stroke="#38bdf8" stroke-width="2.6" stroke-linecap="round"/>`;
        }
        return out;
      };

      const correctFn = drawBilateral(wings, true);
      const distractors = [
        drawBilateral(wings, false),
        drawBilateral(wings - 1, true),
        drawBilateral(wings + 1, false)
      ];

      const allFns = prng.shuffle([correctFn, ...distractors]);
      const correctIndex = allFns.findIndex(fn => fn(60) === correctFn(60));

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 110, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'أي من الأشكال التالية يحقق التماثل الانعكاسي التام (مرآة رأسية متطابقة) عبر محور التناظر المركزي المتقطع؟',
        questionText_en: 'Which of the following figures demonstrates exact bilateral mirror symmetry across the dashed vertical axis?',
        stimulusHtml: wrapSvg(`<g transform="translate(100, 75)">${correctFn(120)}</g>`, '0 0 200 150', 'iq-svg-puzzle', 'Bilateral Symmetry Sample'),
        options,
        correctIndex,
        explanation_ar: 'الشكل الصحيح يمتلك أذرعاً متطابقة تماماً في الطول والارتفاع على جانبي محور التماثل الرأسي.',
        explanation_en: 'The correct option has bilateral symmetry with identical arm lengths on both sides of the mirror axis.'
      };
    }
  },

  // 15. Cross-Grid Color Gradient Inversion
  {
    id: 'abs_color_inversion_matrix',
    domain: 'abstract_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const drawBlock = (fillOuter, fillInner) => (s) => {
        const half = s * 0.42;
        const inner = s * 0.22;
        return `
          <rect x="${-half}" y="${-half}" width="${half * 2}" height="${half * 2}" rx="6" fill="${fillOuter}" stroke="#38bdf8" stroke-width="2"/>
          <circle cx="0" cy="0" r="${inner}" fill="${fillInner}" stroke="#ffffff" stroke-width="1.5"/>
        `;
      };

      const cBlue = '#3b82f6';
      const cPink = '#ec4899';

      // Alternating checkerboard color inversion
      const cells = [
        drawBlock(cBlue, cPink), drawBlock(cPink, cBlue), drawBlock(cBlue, cPink),
        drawBlock(cPink, cBlue), drawBlock(cBlue, cPink), drawBlock(cPink, cBlue),
        drawBlock(cBlue, cPink), drawBlock(cPink, cBlue), drawBlock(cBlue, cPink)
      ];

      const correctFn = drawBlock(cBlue, cPink);
      const distractors = [
        drawBlock(cPink, cBlue),
        drawBlock(cBlue, cBlue),
        drawBlock(cPink, cPink)
      ];

      const allFns = prng.shuffle([correctFn, ...distractors]);
      const correctIndex = allFns.findIndex(fn => fn(60) === correctFn(60));

      const options = allFns.map((fn, idx) => ({
        id: String.fromCharCode(65 + idx),
        svg: renderOptionSvg(fn, 110, `Option ${idx + 1}`)
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'تتناوب ألوان الإطار الخارجي والقرص الداخلي في نمط شطرنجي متعاكس. ما هو الشكل الذي يكمل الخلية الأخيرة؟',
        questionText_en: 'The colors of the outer square and inner circle alternate in a checkerboard pattern. Which tile completes the matrix?',
        stimulusHtml: render3x3Matrix(cells, 310),
        options,
        correctIndex,
        explanation_ar: 'يتبع النمط تعاقباً شطرنجياً؛ والموضع الأخير يجب أن يكون بإطار أزرق ودائرة داخلية وردية.',
        explanation_en: 'Following the checkerboard alternation, the final cell requires a blue outer tile with a pink inner circle.'
      };
    }
  }
];
